import { create } from 'zustand'

import { customsV2Service } from '@/services'
import { ApiResponse } from '@/services/api'
import { ProcessedError } from '@/services/api/errorTypes'
import { readFileAsBase64 } from '@/utils/fileReader'

interface ImportFileState {
  file: File | null
  loading: boolean
  errors: string[]
}

interface ToastMessage {
  type: 'error' | 'success'
  message: string
  lastMasterBillId?: number
}

interface ImportFileActions {
  // 导入相关动作
  loadFile: (file: File) => void
  clearFile: () => void
  doUpload: () => Promise<ApiResponse<{ masterBillId: number }>>
  setErrors: (message: string[]) => void
  clearErrors: () => void
}

type ImportFileStore = ImportFileState & ImportFileActions

// 初始状态
const initialState: ImportFileState = {
  file: null,
  loading: false,
  errors: []
}

const useImportFileStore = create<ImportFileStore>()((set, get) => ({
  ...initialState,
  // 导入相关动作
  loadFile: (file) => set({ file: file }),
  clearFile: () => set({ file: null, errors: [] }),
  setErrors: (value) => set({ errors: value }),
  clearErrors: () => set({ errors: [] }),

  doUpload: async () => {
    set({ loading: true })

    // 使用工具类读取文件
    const fileReadResult = await readFileAsBase64(get().file)
    if (!fileReadResult.success) {
      return {
        success: false,
        message: fileReadResult.error
      }
    }

    try {
      // 调用API上传文件
      const { success, message, data, errors } =
        await customsV2Service.importExcel({
          fileData: fileReadResult.data.base64,
          filename: fileReadResult.data.filename
        })

      if (success) {
        get().clearFile()
        return {
          success,
          message,
          data
        }
      } else {
        set({
          errors: errors
        })

        return {
          success: success,
          message: `导入成功：${message}`
        }
      }
    } catch (error: unknown) {
      let errorMessage = '导入失败'
      if (error instanceof Error) {
        const details = (
          error as unknown as {
            processedError: ProcessedError<Record<'detail', string>>
          }
        ).processedError.details
        if (details) {
          errorMessage = details.detail
        }
      }
      return {
        success: false,
        message: `${errorMessage}`
      }
    } finally {
      set({ loading: false })
    }
  }
}))

export const useImportFileState = () => {
  const file = useImportFileStore((state) => state.file)
  const loading = useImportFileStore((state) => state.loading)
  const errors = useImportFileStore((state) => state.errors)
  return { file, loading, errors }
}

export const useImportFileActions = () => {
  const loadFile = useImportFileStore((state) => state.loadFile)
  const clearFile = useImportFileStore((state) => state.clearFile)
  const doUpload = useImportFileStore((state) => state.doUpload)
  const clearErrors = useImportFileStore((state) => state.clearErrors)
  return { loadFile, clearFile, doUpload, clearErrors }
}
